<template>
    <div class="tutorial-modal">
        <!-- Header -->
        <div class="modal-header">
            <div class="header-content">
                <div class="header-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                        <line x1="7" y1="2" x2="7" y2="22"/>
                        <line x1="17" y1="2" x2="17" y2="22"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <line x1="2" y1="7" x2="7" y2="7"/>
                        <line x1="2" y1="17" x2="7" y2="17"/>
                        <line x1="17" y1="7" x2="22" y2="7"/>
                        <line x1="17" y1="17" x2="22" y2="17"/>
                    </svg>
                </div>
                <div class="header-text">
                    <h2>סרטוני הדרכה</h2>
                    <p>למד כיצד להשתמש באפליקציה בקלות</p>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div class="modal-body">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <span>טוען סרטונים...</span>
            </div>

            <!-- Empty State -->
            <div v-else-if="videos.length === 0" class="empty-state">
                <div class="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <polygon points="23 7 16 12 23 17 23 7"/>
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                    </svg>
                </div>
                <h3>אין סרטונים זמינים כרגע</h3>
                <p>סרטוני ההדרכה יתווספו בקרוב</p>
            </div>

            <!-- Videos Grid -->
            <div v-else class="videos-container">
                <!-- Video Player (when a video is selected) -->
                <div v-if="selectedVideo" class="video-player-section">
                    <button class="back-btn" @click="closePlayer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12"/>
                            <polyline points="12 19 5 12 12 5"/>
                        </svg>
                        <span>חזרה לרשימה</span>
                    </button>

                    <div class="player-wrapper">
                        <!-- YouTube Player -->
                        <iframe
                            v-if="selectedVideo.videoType === 'youtube'"
                            :src="selectedVideo.videoUrl"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowfullscreen
                            class="video-iframe"
                        ></iframe>

                        <!-- Native Video Player -->
                        <video
                            v-else
                            :src="selectedVideo.videoUrl"
                            controls
                            class="video-native"
                        >
                            הדפדפן שלך אינו תומך בתגית וידאו.
                        </video>
                    </div>

                    <div class="video-details">
                        <h3>{{ selectedVideo.title }}</h3>
                        <p v-if="selectedVideo.description">{{ selectedVideo.description }}</p>
                    </div>
                </div>

                <!-- Videos List -->
                <div v-else class="videos-grid">
                    <div
                        v-for="video in videos"
                        :key="video.id"
                        class="video-card"
                        @click="playVideo(video)"
                    >
                        <div class="card-thumbnail">
                            <!-- YouTube Thumbnail -->
                            <img
                                v-if="video.videoType === 'youtube'"
                                :src="getYouTubeThumbnail(video.videoUrl)"
                                :alt="video.title"
                                class="thumbnail-img"
                            />
                            <!-- File Video Thumbnail -->
                            <div v-else class="thumbnail-placeholder">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <polygon points="23 7 16 12 23 17 23 7"/>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                </svg>
                            </div>
                            <div class="play-overlay">
                                <div class="play-button">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <polygon points="5 3 19 12 5 21 5 3"/>
                                    </svg>
                                </div>
                            </div>
                            <div v-if="video.videoType === 'youtube'" class="youtube-badge">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                                </svg>
                            </div>
                        </div>
                        <div class="card-content">
                            <h4 class="card-title">{{ video.title }}</h4>
                            <p v-if="video.description" class="card-description">{{ video.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { collection, getDocs, query, where, orderBy } from "firebase/firestore"
import { db } from '@/firebase/config'

const videos = ref([])
const loading = ref(true)
const selectedVideo = ref(null)

const fetchVideos = async () => {
    try {
        loading.value = true

        // Query active videos ordered by displayOrder
        const videosQuery = query(
            collection(db, "videos"),
            where("isActive", "==", true),
            orderBy("displayOrder", "asc")
        )

        const querySnapshot = await getDocs(videosQuery)
        videos.value = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }))
    } catch (error) {
        console.error('Error fetching tutorial videos:', error)
    } finally {
        loading.value = false
    }
}

const getYouTubeThumbnail = (url) => {
    // Extract video ID from embed URL
    const match = url.match(/embed\/([^?]+)/)
    if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`
    }
    return ''
}

const playVideo = (video) => {
    selectedVideo.value = video
}

const closePlayer = () => {
    selectedVideo.value = null
}

onMounted(() => {
    fetchVideos()
})
</script>

<style scoped>
.tutorial-modal {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f8fafc;
    overflow: hidden;
}

/* Header */
.modal-header {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    padding: 24px 30px;
    flex-shrink: 0;
}

.header-content {
    display: flex;
    align-items: center;
    gap: 18px;
    direction: rtl;
    max-width: 900px;
    margin: 0 auto;
}

.header-icon {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: white;
}

.header-text {
    flex: 1;
}

.header-text h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: white;
}

.header-text p {
    margin: 6px 0 0 0;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.85);
}

/* Body */
.modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    direction: rtl;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 60px 20px;
    color: #64748b;
}

.loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 60px 20px;
    text-align: center;
}

.empty-icon {
    color: #cbd5e1;
}

.empty-state h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #475569;
}

.empty-state p {
    margin: 0;
    font-size: 15px;
    color: #94a3b8;
}

/* Videos Container */
.videos-container {
    max-width: 900px;
    margin: 0 auto;
}

/* Videos Grid */
.videos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
}

/* Video Card */
.video-card {
    background: white;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

.video-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
    border-color: #2563eb;
}

.card-thumbnail {
    position: relative;
    aspect-ratio: 16/9;
    background: #1e293b;
    overflow: hidden;
}

.thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumbnail-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
    color: #64748b;
}

.play-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.video-card:hover .play-overlay {
    opacity: 1;
}

.play-button {
    width: 64px;
    height: 64px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #2563eb;
    transform: scale(0.9);
    transition: transform 0.3s ease;
}

.video-card:hover .play-button {
    transform: scale(1);
}

.play-button svg {
    margin-right: -4px;
}

.youtube-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    background: #ff0000;
    color: white;
    padding: 6px 8px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.card-content {
    padding: 16px 18px;
}

.card-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.card-description {
    margin: 8px 0 0 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Video Player Section */
.video-player-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    color: #475569;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    align-self: flex-start;
}

.back-btn:hover {
    background: #f8fafc;
    border-color: #2563eb;
    color: #2563eb;
}

.player-wrapper {
    width: 100%;
    aspect-ratio: 16/9;
    background: #000;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.video-iframe,
.video-native {
    width: 100%;
    height: 100%;
}

.video-details {
    background: white;
    padding: 20px 24px;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.video-details h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #1e293b;
}

.video-details p {
    margin: 10px 0 0 0;
    font-size: 15px;
    color: #64748b;
    line-height: 1.6;
}

/* Responsive */
@media (max-width: 768px) {
    .modal-header {
        padding: 20px;
    }

    .header-icon {
        width: 50px;
        height: 50px;
    }

    .header-icon svg {
        width: 26px;
        height: 26px;
    }

    .header-text h2 {
        font-size: 20px;
    }

    .header-text p {
        font-size: 14px;
    }

    .modal-body {
        padding: 16px;
        /* Hide scrollbar on mobile but keep scrolling working */
        scrollbar-width: none;      /* Firefox */
        -ms-overflow-style: none;   /* IE / old Edge */
        -webkit-overflow-scrolling: touch;
    }

    .modal-body::-webkit-scrollbar {
        display: none;              /* Chrome, Safari, Samsung Internet, Android WebView */
        width: 0;
        height: 0;
    }

    .videos-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .video-card {
        border-radius: 12px;
    }

    .play-button {
        width: 56px;
        height: 56px;
    }

    .play-button svg {
        width: 26px;
        height: 26px;
    }

    .card-content {
        padding: 14px 16px;
    }

    .card-title {
        font-size: 15px;
    }

    .player-wrapper {
        border-radius: 12px;
    }

    .video-details {
        padding: 16px 18px;
    }

    .video-details h3 {
        font-size: 18px;
    }
}

@media (max-width: 480px) {
    .header-content {
        gap: 14px;
    }

    .header-icon {
        width: 44px;
        height: 44px;
    }

    .header-icon svg {
        width: 22px;
        height: 22px;
    }

    .header-text h2 {
        font-size: 18px;
    }

    .modal-body {
        padding: 12px;
    }

    .back-btn {
        padding: 8px 14px;
        font-size: 14px;
    }
}
</style>
